"use client";

import { motion } from "motion/react";
import {
  Mic,
  Infinity as InfinityIcon,
  Globe,
  Video,
  MessagesSquare,
  Scissors,
  Sliders,
  UserPlus,
} from "lucide-react";
import { useLang } from "@/context/lang";
import { useUpper } from "@/lib/upper";

// Most-important first: voices → unlimited → multi-language → video dubbing → dialogue → editor → controls → cloning
const featureIcons = [Mic, InfinityIcon, Globe, Video, MessagesSquare, Scissors, Sliders, UserPlus];
const featureKeys = ["feat.f1", "feat.f5", "feat.f4", "feat.f6", "feat.f7", "feat.f3", "feat.f2", "feat.f8"];

export function Features() {
  const { T } = useLang();
  const U = useUpper();

  return (
    <section id="ozellikler" className="py-24 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-900">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, type: "spring", stiffness: 200 }}
            className="inline-block text-sm font-bold text-cyan-600 dark:text-cyan-300 tracking-wider bg-cyan-50 dark:bg-cyan-500/15 px-4 py-1.5 rounded-full border border-cyan-100 dark:border-cyan-400/30"
          >
            {U(T("feat.title"))}
          </motion.span>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-neutral-900 dark:text-white mt-5 leading-[1.05]">
            {T("feat.badge")}{" "}
            <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">
              {T("feat.desc")}
            </span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-neutral-600 dark:text-slate-300 max-w-2xl mx-auto font-medium">
            {T("feat.sub")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {featureKeys.map((key, i) => {
            const Icon = featureIcons[i];
            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07, duration: 0.6, type: "spring", stiffness: 100 }}
                whileHover={{ y: -8, scale: 1.02, transition: { type: "spring", stiffness: 300, damping: 22 } }}
                whileTap={{ scale: 0.98 }}
                className="group relative p-6 rounded-2xl border border-neutral-200 dark:border-white/10 hover:border-cyan-200 dark:hover:border-cyan-400/40 hover:shadow-xl hover:shadow-cyan-500/10 transition-all duration-300 bg-white dark:bg-slate-900"
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-50/0 dark:from-cyan-500/0 to-teal-50/0 group-hover:from-cyan-50/50 group-hover:to-teal-50/50 dark:group-hover:from-cyan-500/10 dark:group-hover:to-teal-500/10 transition-all duration-300" />

                <div className="relative z-10">
                  <motion.div
                    whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                    className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-500/15 dark:to-teal-500/10 border border-cyan-100 dark:border-cyan-400/30 flex items-center justify-center mb-5 group-hover:from-cyan-100 group-hover:to-teal-100 dark:group-hover:from-cyan-500/25 dark:group-hover:to-teal-500/20 group-hover:border-cyan-200 dark:hover:border-cyan-400/40 transition-colors duration-300"
                  >
                    <Icon size={22} className="text-cyan-600 dark:text-cyan-300" />
                  </motion.div>
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-white mb-2">
                    {T(`${key}.title`)}
                  </h3>
                  <p className="text-sm text-neutral-600 dark:text-slate-300 leading-relaxed">
                    {T(`${key}.desc`)}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
